import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, ArrowRight } from "lucide-react";
import { ItinerarioStep } from "@/data/itinerarios";
import { blogPosts } from "@/data/blogPosts";

interface ItinerarioTimelineProps {
  steps: ItinerarioStep[];
  itinerarioTitle: string;
}

const ItinerarioTimeline = ({ steps, itinerarioTitle }: ItinerarioTimelineProps) => {
  const navigate = useNavigate();

  const getArticleTitle = (articleId: string): string => {
    const post = blogPosts.find(p => p.id === articleId);
    return post?.title || "Artículo";
  };

  const getArticleSlug = (articleId: string): string => {
    const post = blogPosts.find(p => p.id === articleId);
    return post?.slug || "";
  };

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-border" />

      <div className="space-y-12">
        {steps.map((step, index) => (
          <div key={step.number} className="relative pl-16 md:pl-20">
            {/* Step number circle */}
            <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center z-10">
              <span className="text-lg md:text-xl font-semibold text-primary">{step.number}</span>
            </div>

            <div className="space-y-4">
              {/* Step header */}
              <div>
                <h3 className="text-2xl md:text-3xl font-light text-foreground leading-tight">
                  {step.title}
                </h3>
                <p className="text-base text-muted-foreground font-light mt-2 leading-relaxed">
                  {step.description}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  ⏱ Tiempo estimado: {step.estimatedTime}
                </p>
              </div>

              {/* Related articles */}
              <div className="space-y-2">
                {step.articleIds.map(articleId => {
                  const slug = getArticleSlug(articleId);
                  if (!slug) return null;
                  return (
                    <Link
                      key={articleId}
                      to={`/blog/${slug}`}
                      className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors group/link"
                    >
                      <BookOpen className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="group-hover/link:underline line-clamp-1">{getArticleTitle(articleId)}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Dual CTA */}
              <div className="flex flex-wrap gap-3 pt-2">
                {step.articleIds[0] && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/blog/${getArticleSlug(step.articleIds[0])}`)}
                  >
                    Leer guía
                  </Button>
                )}
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate(`/contacto?tipo=itinerario&paso=${step.number}&ref=${encodeURIComponent(itinerarioTitle)}`)}
                  className="gap-1.5"
                >
                  Que lo haga Globaltis IA
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Separator for non-last items */}
            {index < steps.length - 1 && (
              <div className="border-b border-border/30 mt-8" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ItinerarioTimeline;
